import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Paper } from "@material-ui/core";
import { Alert } from "@mui/material";
import ApproverNameField from "features/activity/components/ApproverNameField/ApproverNameField";
import Authorize from "components/Authorize";
import { hasPermission } from "components/Authorize/authorizeUtil";
import { permissionProfiles } from "components/Authorize/permissionProfiles";
import { permissions } from "components/Authorize/permissions/permissions";
import DateTimeField from "components/DateTimePicker/DateTimePicker";
import NotificationProvider from "components/NotificationProvider";
import { activityCustomFieldSetup, activityStatusOrder, activityStatusReasons, activityStatuses } from "features/activity/activityConsts";
import ActivityStatusAliasView from "features/activityStatusAlias/ActivityStatusAliasView";
import { NoteContent } from "features/notes/NoteContent";
import { ChargeCalculation } from "features/pricing/ChargeCalculation/ChargeCalculation";
import { PricingLookup } from "features/pricing/PricingLookup/PricingLookup";
import { selectIsClient, selectTenantName } from "features/user/userSlice";
import { useGetVendorsQuery } from "features/vendor/vendorSlice";
import { orderBy } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { arrayToObject } from "utils/object-util";
import CustomInput from "../../../../components/CustomInput";
import LoadingSpinner from "../../../../components/LoadingSpinner";
import { useGetActivityStatusReasonsQuery, useGetAllActivitiesQuery, useGetAllActivityItemsWithCustomFieldsQuery, useGetWorkOrderCustomDataQuery, useUpdateActivityLogMutation, useGetActivityItemMaterialsQuery, useGetActivityLogMaterialsQuery } from "../../../activity/activitySlice";
import { useGetLotsQuery } from "../../../lots/lotSlice";
import { getLotsWithTenantText } from "../../../lots/lotUtil";
import { vehicleDetailsPageStyle } from "../VehicleDetailsPageStyle";
import { ActivityItemPricingType } from "features/pricingManagement/PricingManagementConsts";

const EMPTY_ARRAY = [];

const EditActivityLog = ({
    onCancel,
    activityLog,
    onConfirm,
    clientId,
    clientAccountId,
    vehicleTypeId,
    withDialog = true
}) => {

    let { data: activities, error: activityError, isLoading: isLoadingActivity } = useGetAllActivitiesQuery();
    let { data: lots, error: getLotsError, isLoading: loadingLots } = useGetLotsQuery();
    let { data: activityItemsWithCustomFields, error: activityItemError, isLoading: isLoadingActivityItems } = useGetAllActivityItemsWithCustomFieldsQuery();
    let { data: vendors, error: vendorsError, isLoading: isLoadingVendors } = useGetVendorsQuery();
    let { data: activityCustomData, error: workOrderCustomDataError, isLoading: isLoadingworkOrderCustomData } = useGetWorkOrderCustomDataQuery(activityLog.workOrderId);
    let { data: statusReasons, error: statusReasonsError, isLoading: isLoadingStatusReasons } = useGetActivityStatusReasonsQuery();
    statusReasons = statusReasons || [];

    const { data: availableMaterials = EMPTY_ARRAY, isLoading: isLoadingAvailableMaterials } = useGetActivityItemMaterialsQuery(
        activityLog?.activityItemId,
        { skip: !activityLog?.activityItemId }
    );

    const { data: existingLogMaterials = EMPTY_ARRAY, isLoading: isLoadingLogMaterials } = useGetActivityLogMaterialsQuery(
        activityLog?.workOrderId,
        { skip: !activityLog?.workOrderId }
    );

    const classes = vehicleDetailsPageStyle();
    const tenantName = useSelector(selectTenantName);
    const isClient = useSelector(selectIsClient);

    const [dateTime, setDateTime] = useState(activityLog?.formattedDate ?? activityLog?.date);
    const [vendorId, setVendorId] = useState(activityLog?.vendorId);
    const [cost, setCost] = useState(activityLog?.cost);
    const [vendorQuotePrice, setVendorQuotePrice] = useState(activityLog?.vendorQuotePrice ?? null);
    const [estimatedDaysToComplete, setEstimatedDaysToComplete] = useState(activityLog?.estimateDaysToComplete);
    const [revenue, setRevenue] = useState(activityLog?.revenue);
    const [withinSla, setWithinSla] = useState(activityLog?.withinSla);
    const [selectedStatus, setSelectedStatus] = useState(activityLog?.activityId);
    const [selectedLot, setSelectedLot] = useState(activityLog?.lotId);
    const [updateActivityLog, { isLoading }] = useUpdateActivityLogMutation();
    const [note, setNote] = useState();
    const [repairOrder, setRepairOrder] = useState(activityLog?.repairOrder ?? "");
    const [notSupported, setNotSupported] = useState(false);
    const [workorderCustomData, setWorkorderCustomData] = useState(activityCustomData || []);
    const [reasonId, setReasonId] = useState(activityLog?.reasonId);
    const [offSiteWork, setOffSiteWork] = useState(!!activityLog?.offSiteWork);
    const [requestedCompletionDate, setRequestedCompletionDate] = useState(activityLog?.requestedCompletionDate);
    const [noCharge, setNoCharge] = useState(activityLog?.noCharge);
    const [approverName, setApproverName] = useState('');
    const [materials, setMaterials] = useState([]);

    const onChangeNote = (key, val) => {
        switch (key) {
            case "content":
                setNote({ ...note, content: val });
                break;
            case "viewByClient":
                setNote({ ...note, viewByClient: val })
        }
    }

    const activityItemsLookup = arrayToObject(activityItemsWithCustomFields);
    let currentActivityItem = activityItemsLookup?.[activityLog?.activityItemId];

    vendors = vendors || [];
    activities = orderBy(
        activities?.map(a => ({
            ...a,
            order: activityStatusOrder[a.id]
        })),
        'order'
    ) ?? [];

    const onSelectDateTime = (e) => {
        setDateTime(e)
    }

    useEffect(() => {
        if (activityCustomData) {
            setWorkorderCustomData(activityCustomData);
        }
    }, [activityCustomData])

    useEffect(() => {
        if (!isLoadingLogMaterials && !isLoadingAvailableMaterials && availableMaterials.length > 0) {
            const initializedMaterials = availableMaterials.map(availMat => {
                const existingMat = existingLogMaterials.find(
                    logMat => logMat.activityItemMaterialId === availMat.id
                );
                return {
                    id: existingMat?.id || null,
                    activityItemMaterialId: availMat.id,
                    materialName: availMat.materialName,
                    unitOfMeasure: availMat.unitOfMeasure || '',
                    numberOfUnits: existingMat?.numberOfUnits || 0,
                    originalNumberOfUnits: existingMat?.numberOfUnits || 0
                };
            });
            setMaterials(initializedMaterials);
        }
    }, [activityLog?.activityItemId, activityLog?.workOrderId, isLoadingLogMaterials, isLoadingAvailableMaterials]);

    useEffect(() => {
        setDateTime(activityLog?.formattedDate ?? activityLog?.date);
        setVendorId(activityLog?.vendorId);
        setCost(activityLog?.cost);
        setEstimatedDaysToComplete(activityLog?.estimateDaysToComplete);
        setRevenue(activityLog?.revenue);
        setWithinSla(activityLog?.withinSla);
        setSelectedStatus(activityLog?.activityId);
        setSelectedLot(activityLog?.lotId);
        setRepairOrder(activityLog?.repairOrder ?? "");
        setReasonId(activityLog?.reasonId);
        setOffSiteWork(!!activityLog?.offSiteWork);
        setRequestedCompletionDate(activityLog?.requestedCompletionDate);
        setNoCharge(activityLog?.noCharge);
        setApproverName('');
    }, [activityLog]);

    const onChangeStatus = (val) => {
        setSelectedStatus(val);

        if (parseInt(val) === activityStatuses.PendingInvoice ||
            (parseInt(val) === activityStatuses.Completed && activityLog?.activityId != activityStatuses.PendingInvoice)) {
            setDateTime(Date.now());
        }
    }

    const onChangeCustomFieldData = (customField, val) => {
        let customDataList = [...workorderCustomData] || []
        let index = customDataList.findIndex(c => c.activityItemCustomFieldId == customField.id);
        if (index == -1) {
            customDataList = [...customDataList, {
                groupId: activityLog.groupId,
                workOrderId: activityLog.workOrderId,
                activityItemCustomFieldId: customField.id,
                value: val,
                mappedTo: customField?.mappedTo
            }]
        } else {
            customDataList[index] = { ...customDataList[index], ["value"]: val };
        }
        setWorkorderCustomData(customDataList)
    }

    const handleMaterialUnitsChange = (index, value) => {
        const updatedMaterials = [...materials];
        updatedMaterials[index] = {
            ...updatedMaterials[index],
            numberOfUnits: parseFloat(value) || 0
        };
        setMaterials(updatedMaterials);
    };

    const save = async () => {
        if (!dateTime || !moment(dateTime).isValid()) {
            NotificationProvider.error("Invalid activity date");
            return;
        }

        let date = new Date(moment(dateTime).format("MMM DD, yyyy, hh:mm a") + " UTC");
        let activityDate = date.toISOString();

        const materialsToSend = materials
            .filter(m => m.numberOfUnits !== m.originalNumberOfUnits)
            .map(m => ({
                id: m.id,
                activityItemMaterialId: m.activityItemMaterialId,
                numberOfUnits: m.numberOfUnits
            }));

        let data = {
            id: activityLog.activityLogId,
            createdDate: moment(),
            activityDate: activityDate,
            vendorId: vendorId,
            price: cost,
            vendorQuotePrice: vendorQuotePrice !== null && vendorQuotePrice !== "" ? parseFloat(vendorQuotePrice) : null,
            revenue: revenue,
            status: selectedStatus,
            withinSla: withinSla,
            vehicleId: activityLog?.vehicleId,
            clientId: clientId,
            lotId: selectedLot,
            groupId: activityLog.groupId,
            workOrderId: activityLog.workOrderId,
            note: note,
            repairOrder: repairOrder,
            estimateDaysToComplete: !!estimatedDaysToComplete?.length ? estimatedDaysToComplete : null,
            workorderCustomData: workorderCustomData,
            reasonId: selectedStatus == activityStatuses.Exception ? reasonId : null,
            offSiteWork: offSiteWork,
            requestedCompletionDate: requestedCompletionDate,
            noCharge: noCharge,
            approverName: (isL1Transition || isL2Transition) ? approverName : undefined,
            materials: materialsToSend.length > 0 ? materialsToSend : null,
            activityLogBundleId: activityLog?.activityLogBundleId
        }

        const res = await updateActivityLog(data);  //daysCompletedIn: daysCompletedIn, daysToComplete: daysToComplete

        if (res.error) {
            NotificationProvider.error("Failed to update the activity");
        } else {
            NotificationProvider.success("Successfully updated the activity");
            onConfirm && onConfirm({ emailDraft: res?.data?.emailDraft });
        }
    };

    const isL1Transition = activityLog?.activityId == activityStatuses.PendingClientDecision && parseInt(selectedStatus) == activityStatuses.PendingApproval;
    const isL2Transition = activityLog?.activityId == activityStatuses.PendingApproval && parseInt(selectedStatus) == activityStatuses.PricingApprovedByClient;
    const requiresApproverName = (isL1Transition || isL2Transition) && !isClient;

    const lockCost = activityLog?.activityId == activityStatuses.Completed || activityLog?.activityId == activityStatuses.CompleteBilling || activityLog?.activityId == activityStatuses.CompletedAndPaid;
    const lockCharge = activityLog?.activityId == activityStatuses.CompleteBilling || activityLog?.activityId == activityStatuses.CompletedAndPaid;

    const isNoCharge = !!noCharge || !!currentActivityItem?.noCharge;
    const blockCompleting = activityLog?.activityId == activityStatuses.PendingInvoice && (!vendorId || (Number(revenue) <= 0 && !isNoCharge));

    const lacksPermission = !hasPermission(permissions.ACTIVITIES.MANAGE_PENDING_INVOICE.key);
    const shouldFilterComplete = (lacksPermission && !isNoCharge) || blockCompleting;

    let statusValues = shouldFilterComplete
        ? activities?.filter(a => a.id !== activityStatuses.Completed && a.id !== activityStatuses.CompleteBilling && a.id !== activityStatuses.CompletedAndPaid)
        : activities ?? [];

    const includesMaterials = currentActivityItem?.pricingType === ActivityItemPricingType.BASE_PLUS_MATERIALS && materials.length > 0;

    const form = <><Paper className={classes.paper}>
        <div className={classes.input}>
            <CustomInput
                id="lot"
                label="Lot"
                value={selectedLot}
                elementType="dropdown"
                showEmpty={true}
                required={false}
                onChange={val => setSelectedLot(val)}
                values={getLotsWithTenantText(lots, tenantName) ?? []}
            />
        </div>
        <div className={classes.input}>
            <ActivityStatusAliasView activityIds={statusValues.map(s => s.id)}
                clientId={clientId}
                readonly={false}
                value={selectedStatus}
                dropdownValues={statusValues}
                onChange={val => onChangeStatus(val)}
            />
        </div>
        {requiresApproverName && <div className={classes.input}>
            <ApproverNameField value={approverName} onChange={setApproverName} clientAccountId={clientAccountId} />
        </div>}
        {isL2Transition && <div className={classes.input}>
            <CustomInput
                id="approvalAmount"
                label="Approval Amount (Charge)"
                value={revenue ?? ''}
                elementType="input"
                disabled={true}
            />
        </div>}
        {selectedStatus == activityStatuses.Exception && <div className={classes.input}>
            {!reasonId && <div style={{ paddingBottom: '1em' }}><Alert severity="warning">Please select a reason for the exception</Alert></div>}
            <CustomInput
                id="reasonId"
                label="Reason"
                elementType="dropdown"
                values={statusReasons}
                value={reasonId}
                required={selectedStatus == activityStatuses.Exception}
                showEmpty={true}
                onChange={val => setReasonId(val)}
            />
        </div>}
        {currentActivityItem?.activityItemCustomFields?.map(customField =>
            <div className={classes.input}>
                {activityCustomFieldSetup?.[customField?.mappedTo]?.component ?
                    activityCustomFieldSetup?.[customField?.mappedTo]?.component(customField,
                        workorderCustomData?.find(w => w.activityItemCustomFieldId == customField.id)?.value,
                        onChangeCustomFieldData)
                    :
                    <CustomInput
                        id="ddlCustomFieldData"
                        label={customField?.label}
                        elementType="text"
                        value={workorderCustomData?.find(w => w.activityItemCustomFieldId == customField.id)?.value}
                        onChange={(val) => onChangeCustomFieldData(customField, val)}
                    />
                }
            </div>
        )
        }
        <div className={classes.input}>
            <DateTimeField
                label='Activity Date'
                value={dateTime}
                onChange={onSelectDateTime}
            />
        </div>
        <div className={classes.input}>
            <DateTimeField
                label='Due Date'
                value={requestedCompletionDate}
                onChange={(e) => setRequestedCompletionDate(e)}
            />
        </div>
        <div className={classes.input}>
            <CustomInput
                id="estimatedDaysToComplete"
                label="Estimated Days To Complete"
                numberOnly={true}
                value={estimatedDaysToComplete}
                elementType="input"
                onChange={(val) => setEstimatedDaysToComplete(val != "" ? val : 0)}
            />
        </div>
        <Authorize profile={permissionProfiles.ACTIVITY.EDIT_ACTIVITY_INTERNAL_FIELDS}>
            <div className={classes.input}>
                <CustomInput
                    id="ddlVendorId"
                    label="Vendor"
                    elementType="dropdown"
                    values={vendors ?? []}
                    value={vendorId}
                    classStyles={classes.dropdownStyle}
                    showEmpty={true}
                    onChange={setVendorId}
                />
            </div>
        </Authorize>
        {notSupported && <Grid item className={classes.input}>
            <Alert severity="error">This vendor does not offer this service in this location</Alert>
        </Grid>}
        {includesMaterials && (
            <div className={classes.materialsContainer}>
                <h4 className={classes.materialsTitle}>Materials Usage</h4>
                {materials.map((material, index) => (
                    <div key={`${material.activityItemMaterialId}-${index}`} className={classes.materialRow}>
                        <div className={classes.materialInputWrapper}>
                            <CustomInput
                                id={`material-units-${index}`}
                                elementType="input"
                                numberOnly={true}
                                value={material.numberOfUnits > 0 ? material.numberOfUnits : ''}
                                placeholder="0"
                                onChange={(val) => handleMaterialUnitsChange(index, val)}
                                disabled={lockCost}
                            />
                        </div>
                        <div className={classes.materialLabel}>
                            {material.materialName} {material.unitOfMeasure ? (material.numberOfUnits > 1 ? `${material.unitOfMeasure}s` : material.unitOfMeasure) : ''}
                        </div>
                    </div>
                ))}
            </div>
        )}
        <Authorize profile={permissionProfiles.ACTIVITY.EDIT_ACTIVITY_INTERNAL_FIELDS}>
            <div className={classes.input}>
                <Grid container spacing={1} justifyContent="space-between" alignItems="center">
                    <Grid item xs={12}>
                        <CustomInput
                            id="cost"
                            label={`Internal Cost${includesMaterials ? ` (Including Materials)` : ""}`}
                            value={cost}
                            elementType="input"
                            onChange={(val) => setCost(val != "" ? val : 0)}
                            required={!lockCost}
                            disabled={lockCost}
                        />
                    </Grid>

                </Grid>
            </div>
        </Authorize>
        <Authorize profile={permissionProfiles.ASSETS.ASSETS_DETAIL_PAGE_ACTIVITY_LOG_INTERNAL_COST_COLUMN}>
            <div className={classes.input}>
                <Grid container spacing={1} justifyContent="space-between" alignItems="center">
                    <Grid item xs={12}>
                        <CustomInput
                            id="vendorQuotePrice"
                            label="Vendor Quote Price"
                            value={vendorQuotePrice ?? ""}
                            elementType="input"
                            numberOnly={true}
                            onChange={(val) => setVendorQuotePrice(val !== "" ? val : null)}
                        />
                    </Grid>
                </Grid>
            </div>
        </Authorize>
        <Authorize profile={permissionProfiles.ACTIVITY.EDIT_ACTIVITY_INTERNAL_FIELDS}>
            <div className={classes.input}>
                <Grid container spacing={1} justifyContent="space-between">
                    <Grid item xs={9}>
                        <CustomInput
                            id="cost"
                            label="Charge"
                            value={revenue ?? 1.15 * cost}
                            elementType="input"
                            onChange={(val) => setRevenue(val != "" ? val : 0)}
                            disabled={lockCharge}
                        />
                    </Grid>
                    <Grid item>
                        <PricingLookup
                            onChange={(val) => setCost(val != "" ? val : 0)}
                            activityItemId={activityLog?.activityItemId}
                            lotId={selectedLot}
                            vehicleTypeId={vehicleTypeId}
                            clientId={clientId}
                            vendorId={vendorId}
                            setVendorId={setVendorId}
                            price={cost}
                            setNotSupported={setNotSupported}
                            disabled={lockCost || lockCharge}
                            materials={materials}
                            setCharge={setRevenue}
                        />
                    </Grid>
                    {/* <ChargeCalculation
                        onChange={(val) => setRevenue(val != "" ? val : 0)}
                        clientId={clientId}
                        activityType={activityLog?.activityTypeId}
                        lotId={selectedLot}
                        price={cost}
                        disabled={lockCharge}
                    /> */}
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={noCharge}
                                    onChange={(e, value) => setNoCharge(value)}
                                />
                            }
                            label={<div>No Charge Client</div>}
                        />
                    </Grid>
                </Grid>
            </div>
        </Authorize>
        <div className={classes.input}>
            <CustomInput
                id="repairOrder"
                label="Repair Order"
                elementType="text"
                value={repairOrder}
                onChange={(val) => setRepairOrder(val)}
            />
        </div>
        <Authorize profile={permissionProfiles.ACTIVITY.EDIT_ACTIVITY_INTERNAL_FIELDS}>
            <div className={classes.input}>
                {(reasonId == activityStatusReasons.Other && !(note?.content?.replace(/<[^>]+>/g, '')?.trim())) ?
                    <Alert style={{ marginBottom: '1em' }} severity="error">Note is required</Alert> : <div>Note</div>}
                <NoteContent content={note?.content} viewByClient={note?.viewByClient} onChange={onChangeNote} />
            </div>
        </Authorize>
        <FormControlLabel
            control={
                <Checkbox
                    checked={offSiteWork}
                    onChange={(e, value) => setOffSiteWork(value)}
                />
            }
            label={<div>Off-Site Work</div>}
        /><br />
        <Authorize profile={permissionProfiles.ACTIVITY.EDIT_ACTIVITY_INTERNAL_FIELDS}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={withinSla}
                        onChange={(e, value) => (setWithinSla(value))}
                    />
                }
                label={<div>Within SLA?</div>}
            />
        </Authorize>
    </Paper>
    </>

    const actions = <>
        <Button onClick={onCancel} color='primary'>
            Cancel
        </Button>
        <Button
            onClick={save}
            color='primary'
            variant="contained"
            disabled={isLoading || (requiresApproverName && !approverName?.trim()) || (selectedStatus == activityStatuses.Exception && !reasonId) || (reasonId == activityStatusReasons.Other && !(note?.content?.replace(/<[^>]+>/g, '')?.trim()))}>
            {isLoading ? 'Saving...' : 'Save'}
        </Button>
    </>;

    return (<>
        {withDialog ?
            <Dialog open={!!activityLog} onClose={() => onCancel(false)} aria-labelledby='dialog-title' fullWidth maxWidth='lg'>
                <LoadingSpinner loading={isLoading} />
                <DialogTitle id={'dialog-title'} >Edit Activity {activityItemsWithCustomFields?.find(a => a.id == activityLog?.activityItemId)?.name}</DialogTitle>
                <DialogContent>
                    {form}
                </DialogContent>
                <DialogActions>
                    {actions}
                </DialogActions>
            </Dialog> : <div>
                <div>{form}</div>
                <div>{actions}</div>
            </div>
        }</>
    );
};

export default EditActivityLog;
